import { useState, useEffect, useCallback } from 'react';
import { Recorder } from 'type/type';
import { useNavigate, useParams } from 'react-router-dom';
import hubLayout from 'styles/css/hubLayout.module.css';
import { decryptAES256, deleteRecorderByIdx, encryptAES256, getRecorderList, isMobile, postUuidAndSend } from 'api/api';
import Loading from 'components/common/Loading';
import { useModal } from 'components/common/ModalLayout';
import InCopyModal from 'components/common/InCopyModal';
import { sampleVideo } from 'api/bridge';
import { contractStatusLabel } from 'pages/utils/commonUtils';
import { GrRefresh } from "react-icons/gr";


function ProductList() {
  const navigate = useNavigate();
  const { showModal } = useModal();
  const { userId } = useParams();
  const [recorders, setRecorders] = useState<Recorder[]>([]);
  const [totalCount, setTotalCount] = useState<number>(0);
  const [loading, setLoading] = useState(true);
  const [isOpen, setIsOpen] = useState(false);
  const [recorderIdx, setRecorderIdx] = useState(0);

  const inCopyModal = (_recorderIdx: number) => {
    if (!isOpen) {
      // 현재 모달이 열려 있지 않을 때만 히스토리 상태 추가
      window.history.pushState({ modalOpen: true }, '');
    }
    setIsOpen(true);
    setRecorderIdx(_recorderIdx);
  };

  const createUuidAndSend = async (recorder: Recorder) => {
    showModal("confirm", "UUID를 갱신하시겠습니까?", { confirm: false, cancel: false }, 
      async () => {
        const res = await postUuidAndSend(recorder);
        if (res.code === 200) {
          getRecorders();
          showModal("alert", "UUID 갱신에 성공하였습니다.");
        } else {
          showModal("alert", res.message);
        }
      }
    );
  };

  const goViewer = async (recorder: Recorder) => {
    const isApp = isMobile();
    if (isApp) {
      getSampleVideo(recorder);
    } else {
      popWebViewer(recorder);
    }
  }

  function popWebViewer(recorder: Recorder) {
    if (recorder) {
      const REACT_APP_SECRET_KEY = process.env.REACT_APP_SECRET_KEY!;
      const REACT_APP_SECRET_IV = process.env.REACT_APP_SECRET_IV!;
      const hostname = window.location.hostname === "106.10.107.155" ? "106.10.107.155:13383" : window.location.hostname;
      const { mac, ip, tcp_port, id, password, in_ip, in_port, connect_type, ch } = recorder;
      const parameter = `?mac=${mac}&ip=${connect_type === 'IP' ? in_ip : ip}&port=${connect_type === 'IP' ? in_port : tcp_port}&id=${id}&password=${password}&channel=${ch ? ch.length : 16}&currentChannel=0`;
      const AESparameter = encodeURIComponent(encryptAES256(parameter, REACT_APP_SECRET_KEY!, REACT_APP_SECRET_IV!));
      window.open(
        `http://${hostname}/admin/popup/viewer?recorderInfo=${AESparameter}`,
        'webViewer',
        'width=1550,height=890,top=100,left=200,noopener,noreferrer'
      );
    }
  }

  const getSampleVideo = (recorder: Recorder) => {
    setLoading(true);
    try {
      const recorderIp = recorder.connect_type === "IP" ? recorder.in_ip : recorder.ip;
      const recorderPort = recorder.connect_type === "IP" ? recorder.in_port : recorder.tcp_port;
      if (recorderIp && recorderPort) {
        const REACT_APP_SECRET_KEY = process.env.REACT_APP_SECRET_KEY!;
        const REACT_APP_SECRET_IV = process.env.REACT_APP_SECRET_IV!;
        const recorderId = decryptAES256(recorder.id, REACT_APP_SECRET_KEY!, REACT_APP_SECRET_IV!);
        const recorderPassword = decryptAES256(recorder.password, REACT_APP_SECRET_KEY!, REACT_APP_SECRET_IV!);
        sampleVideo(recorder.mac, recorderIp, recorderPort, recorderId, recorderPassword, recorder.reg_name, recorder.connect_type, recorder.model, recorder.hdd_status ? "Normal" : "Error");
      } else {
        showModal("alert", "IP/PORT가 유효하지 않습니다.");
      }
    } catch (error) {
      console.log(error);
    } finally {
      setLoading(false);
    }
  };

  const getRecorders = useCallback(async () => {
    setLoading(true);
    try {
      const recordersResponse = await getRecorderList(0, 0, { user_id: userId, is_registe: 'All', is_health: true });
      if (recordersResponse.code === 200) {
        setRecorders(recordersResponse.data);
        setTotalCount(recordersResponse.total_count);
      } else {
        showModal("alert", recordersResponse.message);
      }
    } catch (error) {
      console.log(error);
    } finally {
      setLoading(false);
    }
  }, [userId]);

  const deleteRecorder = async (recorderIdx: number) => {
    setLoading(true);
    try {
      showModal("confirm", "해당 장비를 삭제하시겠습니까?", {confirm: false, cancel: false},
        async () => {
          const recordersResponse = await deleteRecorderByIdx(recorderIdx);
          if (recordersResponse.code === 200) {
            showModal("alert", "장비 삭제를 완료하였습니다.", {confirm: false, cancel: false},
              () => {
                getRecorders();
              },
              () => {
                getRecorders();
              },
            );
          } else {
            showModal("alert", recordersResponse.message);
          }
        },
        () => {
          console.log("cancel");
        }
      );
    } catch (error) {
      console.log(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getRecorders();
  }, [getRecorders]);
  return (
    <>
      {loading ? <Loading /> : 
        <>
          <div className={`${hubLayout.cnt_box}`} style={{overflowY: "auto"}}>
            <div className={`${hubLayout.acc__cnt}`}>
              <div className={`${hubLayout.sec_title} ${hubLayout.fixed_title}`}>
                <h2>고객 장비 관리</h2>
                <span onClick={() => navigate(-1)}>X</span>
              </div>
              <p className={`${hubLayout.acc_total} ${hubLayout.display_flex}`} style={{ margin: '4rem 0 20px', justifyContent: "space-between" }}>
                <span>총 <strong className={`${hubLayout.tx_blue}`}>{totalCount}</strong>개</span>
                <span onClick={getRecorders}><GrRefresh /></span>
              </p>
              <ul className={`${hubLayout.acc_list}`}>
                {totalCount === 0 ?
                  <li className={`${hubLayout.acc_item}`}>
                    <div className={`${hubLayout.acc_item_info}`}>
                      <dl>
                        <dd style={{textAlign: "center"}}>등록된 장비가 없습니다. 먼저 장비를 등록한 후 이용해 주세요.</dd>
                      </dl>
                    </div>
                  </li>
                :
                  recorders.map((recorder, index) => 
                    <li key={`recorder_${index}`} className={`${hubLayout.acc_item}`}>
                      <div className={`${hubLayout.acc_item_top}`}>
                        <p className={`${hubLayout.name}`}>{recorder.is_registe ? recorder.model : '1차개통'}</p>
                        <p className={`${hubLayout.id}`}>{recorder.mac}</p>
                      </div>
                      <div className={`${hubLayout.acc_item_info}`}>
                        {recorder.is_registe &&
                          <dl style={{ margin: "10px 0" }}>
                            <dt>설치 위치</dt>
                            <dd>{recorder.reg_name}</dd>
                          </dl>
                        }
                        <dl style={{ margin: "10px 0" }}>
                          {recorder.is_registe &&
                            <>
                              <dt>접속방식</dt>
                              <dd>{recorder.connect_type}</dd>
                            </>
                          }
                          <dt>계약상태</dt>
                          <dd>{contractStatusLabel.find(item => item.code === recorder.contract?.contract_status)?.name || ""}</dd>
                        </dl>
                        <dl style={{ margin: "10px 0" }}>
                          <dt>개통 상태</dt>
                          <dd>{recorder.health?.uuid ? "운영중" : "개통중"}</dd>
                        </dl>
                        {(!recorder.is_registe && recorder.uid?.split("|")[0] === "replace") &&
                          <dl style={{ margin: "10px 0" }}>
                            <dd>
                              <div style={{ padding: "0 1rem" }}>
                                해당 장비는 교체 프로세스로 1차 등록된 장비입니다.
                                해당 장비의 개통 진행 시 기존 장비({ recorder.uid?.split("|")[2] })는 삭제 됩니다.
                              </div>
                            </dd>
                          </dl>
                        }
                        <dl className={`${hubLayout.btn_dl}`} style={{ margin: "10px 0 0 0" }}>
                          {recorder.is_registe ?
                            <>
                              <dd><button type="button" className={`${hubLayout.btn} ${hubLayout.medium} ${hubLayout.blue}`} onClick={() => navigate(`/agent/recorder/edit/${recorder.idx}`)}>수정</button></dd>
                              <dd><button type="button" className={`${hubLayout.btn} ${hubLayout.medium} ${hubLayout.blue}`} onClick={() => deleteRecorder(recorder.idx)}>삭제</button></dd>
                              <dd><button type="button" className={`${hubLayout.btn} ${hubLayout.medium} ${hubLayout.blue}`} onClick={() => goViewer(recorder)}>영상</button></dd>
                              {!recorder.is_copy &&
                                <>
                                  <dd><button type="button" className={`${hubLayout.btn} ${hubLayout.medium} ${hubLayout.blue}`} onClick={() => inCopyModal(recorder.idx)}>내부복사</button></dd>
                                  <dd><button type="button" className={`${hubLayout.btn} ${hubLayout.medium} ${hubLayout.blue}`} onClick={() => navigate(`/agent/recorder/replace/${recorder.idx}`)}>교체</button></dd>
                                  <dd><button type="button" className={`${hubLayout.btn} ${hubLayout.medium} ${hubLayout.blue}`} onClick={() => createUuidAndSend(recorder)}>uuid갱신</button></dd>
                                </>
                              }
                            </>
                          :
                            <>
                              {recorder.uid?.split("|")[0] === "replace" ?
                                <dd><button type="button" className={`${hubLayout.btn} ${hubLayout.medium} ${hubLayout.blue}`} onClick={() => navigate(`/agent/recorder/replace/${recorder.uid?.split("|")[1]}/${recorder.contract_no}/${recorder.mac}/${recorder.idx}`)}>개통진행</button></dd>
                              :
                                <dd><button type="button" className={`${hubLayout.btn} ${hubLayout.medium} ${hubLayout.blue}`} onClick={() => navigate(`/agent/recorder/register/${userId}/${recorder.contract_no}/${recorder.mac}/${recorder.idx}`)}>개통진행</button></dd>
                              }
                              <dd><button type="button" className={`${hubLayout.btn} ${hubLayout.medium} ${hubLayout.blue}`} onClick={() => deleteRecorder(recorder.idx)}>삭제</button></dd>
                              <dd><button type="button" className={`${hubLayout.btn} ${hubLayout.medium} ${hubLayout.blue}`} onClick={() => goViewer(recorder)}>영상</button></dd>
                            </>
                          }
                        </dl>
                      </div>
                    </li>
                  ) 
                }
              </ul>
            </div>
          </div>
          <div className={`${hubLayout.btn_group} ${hubLayout.fixed_bottom_title} ${hubLayout.back_white}`}>
            <button type="button" className={`${hubLayout.btn} ${hubLayout.large} ${hubLayout.pink}`} onClick={() => navigate("/agent/menu")}>Home</button>
            <button type="button" className={`${hubLayout.btn} ${hubLayout.large} ${hubLayout.blue}`} onClick={() => navigate(`/agent/recorder/register/${userId}`)}>장비 등록</button>
          </div>
          {isOpen &&
            <InCopyModal isOpen={isOpen} setIsOpen={setIsOpen} recorderIdx={recorderIdx} onConfirm={getRecorders}/>
          }
        </>
      }
    </>
  );
}

export default ProductList;